import { useEffect, useState } from "react";
import useBatchAddToBasket from "../../Universal/PreBasketSummary/hooks/useBatchAddToBasket";
import { BasketItem } from "../../../models/BasketItem";
import { TicketAccess } from "../../../models/TicketAccess";
import { Exercise } from "../../../models/Exercise";
import useCalendarKarnetSchedule from "./useCalendarKarnetSchedule";
import { DEFAULT_KARNET_REPEATS } from "../../CourtGrid/components/CourtReservationModal/CourtTicketSelection/karnetConcept";
import { errorMessageFromApi } from "../../../utils/apiErrorMessages";

export type KarnetStep = "select" | "confirm";

interface UseCalendarKarnetPurchaseProps {
  exerciseDetails: Exercise | null;
  anchorTicketAccess: TicketAccess;
  preBasket: BasketItem[];
  onPurchased: () => void;
}

export default function useCalendarKarnetPurchase({
  exerciseDetails,
  anchorTicketAccess,
  preBasket,
  onPurchased,
}: UseCalendarKarnetPurchaseProps) {
  const { addToBasket } = useBatchAddToBasket();

  const [enabled, setEnabled] = useState(false);
  const [repeats, setRepeats] = useState<number>(DEFAULT_KARNET_REPEATS);
  const [step, setStep] = useState<KarnetStep>("select");
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState("");

  const requiredCapacity = preBasket.reduce((sum, item) => sum + item.qnt, 0);

  const schedule = useCalendarKarnetSchedule({ exerciseDetails, anchorTicketAccess });

  // Odśwież horyzont, gdy karnet jest włączony i zmienił się wybrany zestaw biletów
  useEffect(() => {
    if (enabled && requiredCapacity > 0) {
      schedule.fetchHorizon(requiredCapacity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, requiredCapacity, exerciseDetails?.objectId]);

  const toggleEnabled = (value: boolean) => {
    setEnabled(value);
    setStep("select");
    setAddError("");
    if (!value) {
      schedule.reset();
    }
  };

  const clampedRepeats = schedule.horizonCount > 0 ? Math.min(repeats, schedule.horizonCount) : repeats;
  const selectedTerms = schedule.terms.slice(0, clampedRepeats);
  const availableTerms = selectedTerms.filter((term) => term.status === "available");
  const unavailableCount = selectedTerms.length - availableTerms.length;

  const goToConfirmation = () => setStep("confirm");
  const backToSelect = () => {
    setStep("select");
    setAddError("");
  };

  const confirmPurchase = async (): Promise<boolean> => {
    if (availableTerms.length === 0) return false;

    setIsAdding(true);
    setAddError("");

    const items: BasketItem[] = [];
    availableTerms.forEach((term) => {
      preBasket.forEach((preItem) => {
        items.push({
          ...preItem,
          ticketAccess: [term.ticketAccess],
        });
      });
    });

    const result = await addToBasket(items);
    setIsAdding(false);

    if (result.isError) {
      setAddError(errorMessageFromApi(result));
      return false;
    }

    onPurchased();
    return true;
  };

  return {
    enabled,
    toggleEnabled,
    repeats,
    setRepeats,
    clampedRepeats,
    step,
    goToConfirmation,
    backToSelect,
    schedule,
    requiredCapacity,
    selectedTerms,
    availableTerms,
    unavailableCount,
    isAdding,
    addError,
    confirmPurchase,
  };
}
